import { Circle as CircleStyle, Fill, Icon, RegularShape, Stroke, Style } from 'ol/style';
import { color as d3color } from 'd3-color';
import gdsColor from 'client/utils/constants/GDS/js/color';
import { LINE_STRING, POINT, POLYGON } from 'client/utils/constants/geometryType';
import { CIRCLE, ICON, SQUARE, TRIANGLE } from 'client/utils/constants/symbolName';
import { iconsBaseUrl } from 'client/utils/constants/frontEndConfig';

const color = gdsColor.black;

// Default style to be used if the style provided by the map configuration is not recognised
// This should cover line, polygon, and point assets
export const defaultStyle = {
    fill: new Fill({
        color: d3color(gdsColor.black).copy({ opacity: 0.2 }).formatRgb()
    }),
    stroke: new Stroke({
        color: color,
        width: 2
    }),
    image: new CircleStyle({
        fill: new Fill({
            color: color
        }),
        stroke: new Stroke({
            color: gdsColor.black,
            width: 2
        }),
        radius: 5
    })
};

// Converts an array of styles from the map configuration into their associated OpenLayers classes
export const generateStyles = (styles) => {
    const olStyles = {};
    styles.forEach(style => {
        let styleFunc = null; // some styles will be returned as functions rather than OpenLayers classes
        const id = style?.id;
        const geometryType = style?.geometryType;
        if (id) {
            let styleOpt = {};
            switch (geometryType) {
                case POINT: {
                    /* Even if a layer's style is intended for a geometryType of POINT, it is possible that
                       some assets will have been represented as a LINE_STRING or POLYGON in the data.
                       Therefore, the 'fill' and 'stroke' properties are specified in addition to 'image'
                       so the asset can be rendered on the map regardless of what geometry is used. */
                    styleOpt.image = setImageProperty(style);
                    // If there is no 'image' property in the map configuration,
                    // or if the symbolName is missing/invalid, revert to default style
                    if (!styleOpt.image) {
                        styleOpt = { ...defaultStyle };
                    } else {
                        // If a fill and stroke is not provided by the map configuration, get them from the image property
                        // If the image property does not have a fill or stroke, use the default style
                        const { fill, stroke } = style;
                        const backupFill = style?.image?.fill ? new Fill(style?.image?.fill) : defaultStyle.fill;
                        const backupStroke = style?.image?.stroke ? new Stroke(style?.image?.stroke) : defaultStyle.stroke;
                        styleOpt.fill = fill ? new Fill(fill) : backupFill;
                        styleOpt.stroke = stroke ? new Stroke(stroke) : backupStroke;
                    }
                    break;
                }
                case LINE_STRING: {
                    /* Even if a layer's style is intended for a geometryType of LINE_STRING, it is possible
                       that some assets will have been represented as a POLYGON in the data.
                       This should be rendered without a fill, so nothing extra is required here. */
                    const { stroke } = style;
                    styleOpt.stroke = stroke ? new Stroke(stroke) : null;
                    styleOpt.image = setDefaultImageProperty(style); // just in case a point geometry is used (not expected)
                    break;
                }
                case POLYGON: {
                    /* Even if a layer's style is intended for a geometryType of POLYGON, it is possible that
                       some assets will have been represented as a POINT or a LINE_STRING in the data.
                       To support this, the 'image' property is populated from the map configuration, or
                       (if this is not specified in the map configuration) from a default point style. */
                    const { fill, stroke, imageSource } = style;
                    styleOpt.stroke = stroke ? new Stroke(stroke) : null;
                    styleOpt.image = setImageProperty(style) || setDefaultImageProperty(style);
                    styleFunc = setPolygonTiledFill(imageSource, styleOpt.stroke, styleOpt.image);
                    if (!styleFunc) {
                        styleOpt.fill = fill ? new Fill(fill) : null;
                    }
                    break;
                }
                default:
                    styleOpt = { ...defaultStyle };
                    break;
            }
            olStyles[id] = styleFunc || new Style(styleOpt);
        }
    });
    return olStyles;
};

// Returns an OpenLayers style to represent POINT geometry if valid parameters are supplied by the map configuration
const setImageProperty = (style) => {
    if (style?.image) {
        const opt = { ...style?.image };
        const { fill, stroke } = style?.image;
        opt.fill = fill ? new Fill(fill) : null;
        opt.stroke = stroke ? new Stroke(stroke) : null;
        const symbolName = style?.symbolName;
        switch (symbolName) {
            case CIRCLE:
                return new CircleStyle(opt);
            case SQUARE:
            case TRIANGLE:
                return new RegularShape(opt);
            case ICON: {
                const imgFileName = style?.image?.src;
                opt.src = new URL(imgFileName, iconsBaseUrl).toString();
                return new Icon(opt);
            }
        }
    }
    return null;
};

// Returns an OpenLayers square that can be used to display an asset that is represented as a POINT
const setDefaultImageProperty = (style) => {
    const { fill, stroke } = style;
    const opt = { };
    opt.fill = fill ? new Fill(fill) : null;
    opt.stroke = stroke ? new Stroke(stroke) : null;
    opt.points = 4;
    opt.radius = 5;
    opt.angle = 0.7853981634;
    opt.displacement = [0, 0];
    opt.rotateWithView = false;
    opt.rotation = 0;
    return new RegularShape(opt);
};

// If an imageSource is specified by the map configuration, use it to create a tiled fill for a polygon
const setPolygonTiledFill = (imageSource, stroke, image) => {
    let styleFunc = null;
    if (imageSource) {
        styleFunc = (feature) => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            const img = new Image();
            img.src = new URL(imageSource, iconsBaseUrl).toString();
            img.onload = () => { // https://stackoverflow.com/questions/36835929/in-openlayers-3-how-to-fill-the-polygon-with-a-picture-from-a-url
                const pattern = context.createPattern(img, 'repeat');
                feature.setStyle(new Style({
                    fill: new Fill({ color: pattern }),
                    stroke: stroke,
                    image: image
                }));
            };
            return new Style(); // return an empty style as will be overwritten once the image has loaded anyway
        };
    }
    return styleFunc;
};

export default { defaultStyle, generateStyles };
